import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { mergeRegister } from '@lexical/utils';
import {
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_LOW,
  SELECTION_CHANGE_COMMAND,
} from 'lexical';
import { useCallback, useEffect, useState } from 'react';

import { HighlightColor } from './highlightConstants';
import { $findHighlightNode } from './HighlightPlugin';

/**
 * Tracks the highlight color of the node under the caret. Returns `null` when
 * the selection is not inside a highlight (or when there is no range selection).
 */
export function useActiveHighlightColor(): HighlightColor | null {
  const [editor] = useLexicalComposerContext();
  const [activeHighlightColor, setActiveHighlightColor] =
    useState<HighlightColor | null>(null);

  const update = useCallback(() => {
    const selection = $getSelection();
    if (!$isRangeSelection(selection)) return;
    setActiveHighlightColor(
      $findHighlightNode(selection.anchor.getNode())?.getColor() ?? null
    );
  }, []);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          update();
        });
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          update();
          return false;
        },
        COMMAND_PRIORITY_LOW
      )
    );
  }, [editor, update]);

  return activeHighlightColor;
}
